import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { ghs, num, formatDate, todayISODate } from "../lib/format";
import { Plus, Trash, X, ArrowsClockwise } from "@phosphor-icons/react";

const DEFAULT = { date: todayISODate(), supplier: "", litres_received: "", cost_per_litre: "", total_cost: "", notes: "" };

export default function Deliveries() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(DEFAULT);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/deliveries");
    setRows(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => {
    const v = e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: v };
      if ((k === "litres_received" || k === "cost_per_litre") && next.litres_received && next.cost_per_litre) {
        next.total_cost = (parseFloat(next.litres_received) * parseFloat(next.cost_per_litre)).toFixed(2);
      }
      return next;
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      await api.post("/deliveries", {
        ...form,
        date: new Date(form.date).toISOString(),
        litres_received: parseFloat(form.litres_received),
        cost_per_litre: parseFloat(form.cost_per_litre),
        total_cost: form.total_cost ? parseFloat(form.total_cost) : null,
      });
      setOpen(false);
      setForm(DEFAULT);
      await load();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this delivery? This affects stock balance.")) return;
    await api.delete(`/deliveries/${id}`);
    await load();
  };

  const totalL = rows.reduce((s, r) => s + r.litres_received, 0);
  const totalCost = rows.reduce((s, r) => s + r.total_cost, 0);

  return (
    <div data-testid="deliveries-page">
      <PageHeader
        subtitle="Replenishment ledger"
        title="Fuel Deliveries"
        action={
          <button onClick={() => setOpen(true)} data-testid="new-delivery-button" className="bg-ps-blue text-white px-4 py-2.5 rounded-md font-semibold flex items-center gap-2 hover:bg-ps-deep transition-colors shadow-sm">
            <Plus size={16} weight="bold" /> New delivery
          </button>
        }
      />

      <div className="px-8 py-4 bg-slate-100 border-b border-slate-200 flex gap-8">
        <div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Records</div><div className="font-mono-data tabular text-xl">{rows.length}</div></div>
        <div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Litres received</div><div className="font-mono-data tabular text-xl text-ps-blue">{num(totalL)}</div></div>
        <div><div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Total spend</div><div className="font-mono-data tabular text-xl">{ghs(totalCost)}</div></div>
      </div>

      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-[0.2em] text-slate-600 border-b border-slate-200">
              <tr>
                <th className="px-3 py-3 text-left">Date</th>
                <th className="px-3 py-3 text-left">Supplier</th>
                <th className="px-3 py-3 text-right">Litres</th>
                <th className="px-3 py-3 text-right">Cost / L</th>
                <th className="px-3 py-3 text-right">Total (GHS)</th>
                <th className="px-3 py-3 text-left">Notes</th>
                <th className="px-3 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="deliveries-table-body">
              {loading && <tr><td colSpan={7} className="p-6"><div className="indus-bar w-1/3" /></td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">No deliveries yet.</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="hover:bg-slate-50">
                  <td className="px-3 py-2 font-mono-data text-xs">{formatDate(r.date)}</td>
                  <td className="px-3 py-2 font-semibold">{r.supplier}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular text-ps-blue">{num(r.litres_received)}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular">{r.cost_per_litre.toFixed(2)}</td>
                  <td className="px-3 py-2 text-right font-mono-data tabular">{ghs(r.total_cost)}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{r.notes}</td>
                  <td className="px-3 py-2"><button onClick={() => del(r.id)} data-testid={`delete-delivery-${r.id}`} className="text-slate-400 hover:text-red-600"><Trash size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex justify-end" onClick={() => setOpen(false)} data-testid="delivery-dialog">
          <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white h-full overflow-y-auto border-l border-slate-200">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">Replenish</div>
                <h2 className="font-heading font-black text-2xl tracking-tight">Record delivery</h2>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="text-slate-500"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <FormField label="Date *" type="date" value={form.date} onChange={onChange("date")} testid="delivery-date" required />
              <FormField label="Supplier *" value={form.supplier} onChange={onChange("supplier")} testid="delivery-supplier" required />
              <FormField label="Litres received *" type="number" step="0.01" value={form.litres_received} onChange={onChange("litres_received")} testid="delivery-litres" required />
              <FormField label="Cost per litre (GHS) *" type="number" step="0.01" value={form.cost_per_litre} onChange={onChange("cost_per_litre")} testid="delivery-cpl" required />
              <FormField label="Total cost (GHS)" type="number" step="0.01" value={form.total_cost} onChange={onChange("total_cost")} testid="delivery-total" />
              <FormField label="Notes" value={form.notes} onChange={onChange("notes")} testid="delivery-notes" />
              {err ? <div className="border border-red-300 bg-red-50 text-red-700 text-sm px-3 py-2">{err}</div> : null}
            </div>
            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100">Cancel</button>
              <button type="submit" data-testid="delivery-submit" className="flex-1 bg-ps-blue text-white py-2.5 rounded-md text-sm font-semibold hover:bg-ps-deep transition-colors">Save delivery</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

function FormField({ label, testid, ...props }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input {...props} data-testid={testid} className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-slate-900 font-mono-data" />
    </div>
  );
}
